import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Modal from "../generic/modal";
import { ERROR_TOAST_CONFIG } from "../../helpers/functions";
import { RevSwitch } from "../ds";

const TOAST = { ...ERROR_TOAST_CONFIG, autoClose: 8000 };

const Aviso = ({ title, text }) => (
    <div>
        <strong>{title}</strong>
        {text && <div>{text}</div>}
    </div>
);

/**
 * Terminar subida y activar selfies (announce-complete) desde cualquier pantalla del evento: el resultado y
 * los errores del backend salen como toasts, así que la cabecera no necesita hueco para una
 * alerta. `announce` es el thunk `announceEventComplete` que la pantalla recibe por connect.
 */
export const useAnnounceApp = ({ eventId, code, announce, formatDate = value => value }) => {
    const { t } = useTranslation();
    const [announcing, setAnnouncing] = useState(false);

    const run = async () => {
        if (announcing || typeof announce !== 'function') return undefined;
        setAnnouncing(true);
        try {
            const result = await announce(eventId);
            if (result?.noticeSkipped) {
                toast.info(<Aviso title={t("eventPhotos.completeGallery.flash.skipped.title")}
                    text={t("eventPhotos.completeGallery.flash.skipped.body", { date: formatDate(result?.lastNoticeDate || result?.completedDate) })} />, TOAST);
            } else if (result?.recipients > 0) {
                toast.success(<Aviso title={t("eventPhotos.completeGallery.flash.sent.title", { count: result.recipients })}
                    text={t("eventPhotos.completeGallery.flash.sent.body", { code })} />, TOAST);
            } else {
                toast.success(<Aviso title={t("eventPhotos.completeGallery.flash.none.title")}
                    text={t("eventPhotos.completeGallery.flash.none.body")} />, TOAST);
            }
            return result;
        } catch (error) {
            const status = error?.response?.status;
            if (status === 400) {
                toast.warning(<Aviso title={t("eventPhotos.completeGallery.error.noFaces.title")}
                    text={t("eventPhotos.completeGallery.error.noFaces.body")} />, TOAST);
            } else if (status === 403) {
                toast.error(<Aviso title={t("eventPhotos.completeGallery.error.generic.title")}
                    text={t("eventPhotos.completeGallery.error.forbidden")} />, TOAST);
            } else if (status === 404) {
                toast.error(<Aviso title={t("eventPhotos.completeGallery.error.generic.title")}
                    text={t("eventPhotos.completeGallery.error.notFound")} />, TOAST);
            } else {
                toast.error(<Aviso title={t("eventPhotos.completeGallery.error.generic.title")}
                    text={t("eventPhotos.completeGallery.error.generic.body")} />, TOAST);
            }
            return undefined;
        } finally {
            setAnnouncing(false);
        }
    };

    return { announcing, announce: run };
};

const Casilla = ({ id, checked, onChange, children }) => (
    <label className="rev-switch-field" htmlFor={id}>
        <input type="checkbox" id={id} className="rev-switch-input" checked={checked} onChange={e => onChange(e.target.checked)} />
        <RevSwitch on={checked} />
        <span>{children}</span>
    </label>
);

/**
 * Confirmación de «Terminar subida y activar selfies»: tres casillas obligatorias (aviso, cierre de la
 * subida, código bloqueado) y una cuarta si el evento no tiene precio. No habla de la galería
 * pública: esa es «Publicar evento», con su propio interruptor, y no depende de esto.
 */
const PublishAppModal = ({ eventId, event, onConfirm, onCancel, loading }) => {
    const { t } = useTranslation();
    const [ackNotice, setAckNotice] = useState(false);
    const [ackUploads, setAckUploads] = useState(false);
    const [ackCode, setAckCode] = useState(false);
    const [ackFree, setAckFree] = useState(false);

    const unitPrice = event?.pricingData?.unitPrice;
    const hasPrice = unitPrice !== undefined && unitPrice !== null && unitPrice !== "";
    const acknowledged = ackNotice && ackUploads && ackCode && (hasPrice || ackFree);

    // El enlace vive dentro del <label> de la casilla: sin parar la propagación, pulsarlo
    // marcaría también la casilla antes de navegar.
    const enlace = (to) => <Link to={to} onClick={e => e.stopPropagation()} key="0" />;

    return (
        <Modal
            title={t("eventPhotos.completeGallery.publishModal.title")}
            showConfirmButton={true}
            showCancelButton={true}
            cancelButtonText={t("generic.alertButton.cancel")}
            confirmButtonText={t("eventPhotos.completeGallery.publishModal.confirm")}
            confirmDisabled={!acknowledged}
            customComponent={
                <>
                    <Casilla id="ackNotice" checked={ackNotice} onChange={setAckNotice}>
                        {t("eventPhotos.completeGallery.publishModal.ackNotice")}
                    </Casilla>
                    <Casilla id="ackUploads" checked={ackUploads} onChange={setAckUploads}>
                        {t("eventPhotos.completeGallery.publishModal.ackUploads")}
                    </Casilla>
                    <Casilla id="ackCode" checked={ackCode} onChange={setAckCode}>
                        <Trans i18nKey="eventPhotos.completeGallery.publishModal.ackCode"
                            values={{ code: event?.code }} components={[enlace(`/events/${eventId}/basic`)]} />
                    </Casilla>
                    {!hasPrice && (
                        <Casilla id="ackFree" checked={ackFree} onChange={setAckFree}>
                            <Trans i18nKey="eventPhotos.completeGallery.publishModal.ackFree"
                                components={[enlace(`/events/${eventId}/pricing`)]} />
                        </Casilla>
                    )}
                </>
            }
            onConfirm={() => { if (acknowledged) onConfirm(); }}
            onCancel={onCancel}
            loading={loading}
        />
    );
};

export default PublishAppModal;
